import { CONSTANTS } from '../_data/lookups.js';
import { extractResourceTypeHandleFromRole, getBindableInputRoleNames, getOutputRoleSpecEntriesFromTool, } from './workflowExtraction.js';
export function isInstantiationTool(tool) {
    return tool.isTemplate !== true;
}
export function isResourceTypeHandleBackedRoleOnlyTool(tool) {
    const roles = [
        ...Object.values(tool.roleSpec.inputRoleByName),
        ...Object.values(tool.roleSpec.outputRoleByName),
    ];
    return roles.every((role) => extractResourceTypeHandleFromRole(role) !== null);
}
// ATTENTION: the slot-eligibility functions below can currently handle only resourceTypeHandle-backed roles. Callers must ensure that the provided toolMaps are pre-filtered accordingly.
export function deriveLoopFeedbackBindingSpec(tool) {
    const compatibleBindings = [];
    for (const targetInputRoleName of getBindableInputRoleNames(tool)) {
        const inputRole = tool.roleSpec.inputRoleByName[targetInputRoleName];
        if (!inputRole)
            continue;
        const inputType = extractResourceTypeHandleFromRole(inputRole);
        if (inputType === null)
            continue;
        for (const [sourceOutputRoleName, outputRole] of getOutputRoleSpecEntriesFromTool(tool)) {
            if (sourceOutputRoleName === CONSTANTS.Cosmos.RoleNames.ErrorOutput)
                continue;
            const outputType = extractResourceTypeHandleFromRole(outputRole);
            if (outputType !== inputType)
                continue;
            compatibleBindings.push({
                sourceOutputRoleName,
                targetInputRoleName,
            });
        }
    }
    return compatibleBindings.length === 1 ? compatibleBindings[0] ?? null : null;
}
export function isLoopFeedbackBindingSpecCompatible(tool, loopFeedbackBindingSpec) {
    if (!getBindableInputRoleNames(tool).includes(loopFeedbackBindingSpec.targetInputRoleName)) {
        return false;
    }
    const inputRole = tool.roleSpec.inputRoleByName[loopFeedbackBindingSpec.targetInputRoleName];
    const outputRole = tool.roleSpec.outputRoleByName[loopFeedbackBindingSpec.sourceOutputRoleName];
    if (!inputRole || !outputRole)
        return false;
    if (loopFeedbackBindingSpec.sourceOutputRoleName === CONSTANTS.Cosmos.RoleNames.ErrorOutput) {
        return false;
    }
    const inputType = extractResourceTypeHandleFromRole(inputRole);
    const outputType = extractResourceTypeHandleFromRole(outputRole);
    return inputType !== null && inputType === outputType;
}
export function getLoopStepWhatSlotEligibleToolHandles(toolMap) {
    const candidates = [];
    for (const tool of toolMap.values()) {
        if (deriveLoopFeedbackBindingSpec(tool) === null)
            continue;
        candidates.push(tool.handle);
    }
    return candidates;
}
/**
 * Returns Tools that can be used as BranchStep whatTools.
 *
 * A Tool is "branch-step-eligible" if:
 * - it has exactly one output role
 * - there's also a cross-case requirement that the whatTool's output role types must be the same (this is enforced dynamically at buildtime)
 */
export function getBranchStepWhatSlotEligibleToolHandles(toolMap) {
    const candidates = [];
    for (const tool of toolMap.values()) {
        const outputEntries = Object.entries(tool.roleSpec.outputRoleByName);
        if (outputEntries.length !== 1)
            continue;
        candidates.push(tool.handle);
    }
    return candidates;
}
/**
 * Returns Tools that can be used as MacroStep whenTools.
 *
 * A Tool is "when-slot-eligible" if:
 * - it has exactly two input roles
 * - it has exactly one output role and that output role is of type `TYPE-Boolean`
 * - all input role types are available in workflowState at the point of execution (this is enforced dynamically at buildtime)
 */
export function getMacroStepWhenSlotEligibleToolHandles(toolMap) {
    const candidates = [];
    for (const tool of toolMap.values()) {
        const inputEntries = Object.entries(tool.roleSpec.inputRoleByName);
        if (inputEntries.length !== 2)
            continue;
        const outputEntries = Object.entries(tool.roleSpec.outputRoleByName);
        if (outputEntries.length !== 1)
            continue;
        const firstOutputEntry = outputEntries[0];
        if (!firstOutputEntry)
            continue;
        const outputRole = firstOutputEntry[1];
        const outputType = extractResourceTypeHandleFromRole(outputRole);
        if (outputType !== CONSTANTS.Handles.ResourceTypes.TYPE_Boolean)
            continue;
        candidates.push(tool.handle);
    }
    return candidates;
}
export function getFilteredToolMap(toolMap, filters = {
    instantiationOnly: true,
    resourceTypeHandleBackedRoleOnly: true,
}) {
    const filteredEntries = Array.from(toolMap.entries()).filter(([, tool]) => {
        return ((!filters.instantiationOnly || isInstantiationTool(tool))
            && (!filters.resourceTypeHandleBackedRoleOnly || isResourceTypeHandleBackedRoleOnlyTool(tool)));
    });
    return new Map(filteredEntries);
}
